import React, { ReactNode } from "react";
import { motion } from "framer-motion";
import { NoItemsText } from "./styles";
import KLButton from "../KLButton";
import KLEmptyState from "../KLEmptyState";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import {
  listContainerVariants,
  listItemVariants,
} from "../../animations/listAnimations";

export interface KLListManagerProps<T> {
  items: T[];
  term?: string;
  pluralTerm: string;
  onAdd?: () => void;
  noItemsText: string;
  emptyStateIllustration?: React.ReactNode;
  emptyStateDescription?: string;
  renderItem: (item: T, index: number, array: T[], term?: string) => ReactNode;
  getItemKey?: (item: T, index: number) => string | number;
}

const KLListManager = <T,>({
  items,
  term,
  pluralTerm,
  onAdd,
  noItemsText,
  emptyStateIllustration,
  emptyStateDescription,
  renderItem,
  getItemKey = (_, index) => index,
}: KLListManagerProps<T>) => {
  const reducedMotion = useReducedMotion();

  return (
    <div>
      {items.length > 0 ? (
        <motion.div
          variants={reducedMotion ? undefined : listContainerVariants}
          initial="hidden"
          animate="visible"
        >
          {items.map((item, index, array) => (
            <motion.div
              key={getItemKey(item, index)}
              variants={reducedMotion ? undefined : listItemVariants}
            >
              {renderItem(item, index, array, term)}
            </motion.div>
          ))}
        </motion.div>
      ) : emptyStateIllustration ? (
        <KLEmptyState
          illustration={emptyStateIllustration}
          title={noItemsText}
          description={emptyStateDescription}
          actionButtonTitle={onAdd ? `הוסיפו עוד ${pluralTerm}` : undefined}
          onActionClick={onAdd}
          size="medium"
        />
      ) : (
        <>
          <NoItemsText data-testid="no-items-message">{noItemsText}</NoItemsText>
          {onAdd && (
            <KLButton
              title={`הוסיפו עוד ${pluralTerm}`}
              onClick={onAdd}
              secondary
            />
          )}
        </>
      )}
      {items.length > 0 && onAdd && (
        <KLButton
          title={`הוסיפו עוד ${pluralTerm}`}
          onClick={onAdd}
          secondary
        />
      )}
    </div>
  );
};

export default KLListManager;
